import { schoolProgramEdLevels } from '@cpd-app/core-module/models';
import { programGoalLine } from '@career-path-decide-nx/util-functions';

import { GoalRow } from './goal-set-dialog.component';

/**
 * GR-15. The goal checklist, as a pure function of the goals payload.
 *
 * WHY THIS MOVED OUT OF THE CONTAINER. `cpd-goal-set-dialog` is the
 * confirmation the product shows after a goal is set, and it was mounted in
 * exactly one place: the schools-and-programs step. So the 3/3 counter in the
 * top bar could not reach it, and opened the LEGACY dialog instead -- the one
 * the team migrated away from and never repointed.
 *
 * The blocker was never the dialog. It was that eighty lines of row-building
 * lived inside that one container, reading two of its observables. A second
 * mount point would have meant a second copy of these rules, which is how the
 * counter and the step end up disagreeing about what "3 of 3" means.
 *
 * So the rules live here, the two callers pass the same payload, and there is
 * one answer to "which goals are set".
 *
 * The shapes are declared inline rather than imported from `generated.ts`:
 * `GetGoals` is a query result whose type is regenerated against the deployed
 * gateway, and this file cares only about the five fields it reads.
 */

/** The fields of the goals payload these rules actually read. */
export interface GoalsForRows {
  occupationTitle?: string | null;
  schoolTitle?: string | null;
  licenseTitle?: string | null;
  certificationTitle?: string | null;
  credentialTitle?: string | null;
  schoolProgramTitle?: string | null;
  schoolProgramEdLevelId?: number | null;
}

/**
 * The programme goal as ONE line: the level, then the SHORT program name
 * (`programGoalLine`, the same line the brag sheet gets from the server).
 *
 * Exactly one program name, never a list (ENRICH.6). The server already sends
 * the name through `displayProgramName` (the shared short-style function);
 * running it again here costs nothing and covers a goal cached before that
 * shipped: a hand-mapped "Master of Science in Occupational Safety
 * Management" loses the qualifier and takes the level word instead, so the
 * degree is never said twice.
 *
 * If the name's own degree CONTRADICTS the goal's level it is kept whole, and
 * shown as it is rather than printing a level the program name disagrees with.
 */
export function credentialLine(goals: GoalsForRows | null | undefined): string {
  const held = goals ?? {};
  const title = held.schoolProgramTitle?.trim();

  if (!title) {
    return '';
  }

  const levelId = held.schoolProgramEdLevelId ?? -1;
  const known = schoolProgramEdLevels.some((item) => item.edLevelId === levelId);

  // The shared one-line form: "Bachelor's in <short name>". An unknown level
  // shows the title as it is.
  return known ? programGoalLine(title, null, levelId) : title;
}

/**
 * The five rows, in the order the dialog lists them.
 *
 * Field names are the ones `GetGoals` ACTUALLY returns. The school row once
 * read `institutionName`, which is not on the payload -- it is `schoolTitle` --
 * so the dialog reported "None selected" for a school goal the member had just
 * set.
 */
export function goalRows(goals: GoalsForRows | null | undefined): GoalRow[] {
  const held = goals ?? {};

  const rows: GoalRow[] = [
    {
      key: 'career',
      label: 'Career goal',
      value: held.occupationTitle?.trim() ?? ''
    },
    /*
     * CRED.1. A Credential Registry credential, set from the occupation page's
     * Relevant Credentials tab.
     *
     * OPTIONAL, like licensure: the Goals Report is complete without one.
     * It sits directly under the career goal because that is what it is about
     * -- a faster route toward the same job -- rather than at the bottom with
     * the things a member has not done.
     *
     * Falls back to certificationTitle so a member who set one through the old
     * certification picker still sees their goal here rather than an empty row
     * next to a filled card on their report.
     */
    {
      key: 'credential',
      label: 'Credential goal',
      value:
        held.credentialTitle?.trim() || held.certificationTitle?.trim() || '',
      isOptional: true
    },
    { key: 'program', label: 'Program goal', value: credentialLine(held) },
    {
      key: 'school',
      label: 'School goal',
      value: held.schoolTitle?.trim() ?? ''
    },
    {
      key: 'licensure',
      label: 'Licensure goal',
      /*
       * CRED.1 REMOVED THE certificationTitle FALLBACK THAT WAS HERE. It
       * predated a credential row existing at all, and leaving it would print
       * the same certification on two rows of a five-row list under two
       * different names.
       */
      value: held.licenseTitle?.trim() || '',
      isOptional: true
    }
  ];

  /*
   * Exactly one Next: the first of the three the report needs that is still
   * empty. Licensure and credential are optional, so neither is ever Next --
   * pointing a member at an optional goal as their next step would be telling
   * them to do something nobody requires.
   */
  const next = rows.find(
    (row) => row.key !== 'licensure' && row.key !== 'credential' && !row.value
  );

  if (next) {
    next.isNext = true;
  }

  return rows;
}
